import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private currentVersion: string = '';
  private checkSubscription: Subscription | null = null;
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  startVersionCheck(): void {
    this.fetchVersion().then(version => {
      this.currentVersion = version;
      this.scheduleChecks();
    });
  }

  private scheduleChecks(): void {
    this.ngZone.runOutsideAngular(() => {
      this.checkSubscription = interval(this.CHECK_INTERVAL_MS).pipe(
        switchMap(() => {
          const cacheBuster = `?t=${Date.now()}`;
          return this.http.get<{ version: string }>(`version.json${cacheBuster}`);
        }),
        catchError(() => EMPTY)
      ).subscribe(response => {
        if (this.currentVersion && response.version !== this.currentVersion) {
          this.ngZone.run(() => this.onVersionMismatch());
        }
      });
    });
  }

  private async fetchVersion(): Promise<string> {
    try {
      const cacheBuster = `?t=${Date.now()}`;
      const response = await this.http
        .get<{ version: string }>(`version.json${cacheBuster}`)
        .toPromise();
      return response?.version || '';
    } catch {
      return '';
    }
  }

  private onVersionMismatch(): void {
    if (confirm('A new version of the application is available. Reload to update?')) {
      window.location.reload();
    } else {
      this.currentVersion = '';
      this.fetchVersion().then(v => this.currentVersion = v);
    }
  }

  stopVersionCheck(): void {
    this.checkSubscription?.unsubscribe();
  }
}
